import React, { useEffect, useMemo, useRef, useState } from "react";

type Props = {
  text: string;
  stickyTop?: string;
  minOpacity?: number;
  softness?: number;
  className?: string;

  /**
   * If true, the section height is calculated automatically
   * so there's no extra empty white scroll after animation ends.
   */
  autoHeight?: boolean;

  /**
   * How many words to reveal per viewport height.
   * Smaller = shorter section, larger = more scroll space.
   */
  wordsPerVh?: number; // e.g. 1.6
};

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

export default function ScrollWordReveal({
  text,
  stickyTop = "18vh",
  minOpacity = 0.18,
  softness = 1,
  className,
  autoHeight = true,
  wordsPerVh = 1.6,
}: Props) {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);

  const words = useMemo(() => text.trim().split(/\s+/), [text]);

  // ✅ Auto height based on word count
  const sectionHeightVh = useMemo(() => {
    if (!autoHeight) return 180;
    const base = 110; // minimum height so it still feels premium
    const extra = Math.ceil(words.length / wordsPerVh);
    return Math.min(240, base + extra); // cap so it doesn't get huge
  }, [autoHeight, words.length, wordsPerVh]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    let raf = 0;

    const update = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;

      const start = vh * 0.6;
      const end = vh * 0.9;

      const total = rect.height - (end - start);
      const current = start - rect.top;

      const p = total <= 0 ? 0 : clamp01(current / total);
      setProgress(p);

      raf = requestAnimationFrame(update);
    };

    raf = requestAnimationFrame(update);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      ref={sectionRef}
      className={className}
      style={{
        background: "#fff",
        color: "#000",
        height: `${sectionHeightVh}vh`,
        position: "relative",
      }}>
      <div
        style={{
          position: "sticky",
          top: stickyTop,
          padding: "0 6vw",
          maxWidth: 1100,
          margin: "0 auto",
        }}>
        <p
          style={{
            // fontSize: "clamp(22px, 5vw, 40px)",
            fontSize: "clamp(32px, 5vw, 64px)",
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            margin: 0,
            fontWeight: 500,
          }}>
          {words.map((w, i) => {
            const n = Math.max(words.length, 1);
            const step = 1 / n;
            const t = i * step;

            const local = clamp01((progress - t) / (step * softness));
            const opacity = minOpacity + local * (1 - minOpacity);

            return (
              <span
                key={`${w}-${i}`}
                style={{
                  opacity,
                  transition: "opacity 40ms linear",
                }}>
                {w}
                {i !== n - 1 ? " " : ""}
              </span>
            );
          })}
        </p>
      </div>
    </div>
  );
}
